import {
    ChangeDetectionStrategy,
    Component,
    HostBinding,
    Input,
    computed,
    DestroyRef,
    ElementRef,
    Injector,
    OnDestroy,
    OnInit,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MineIconDirective } from '@mineos/mine-ui';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { filter, switchMap, take, timer } from 'rxjs';

import { TippyModule } from 'src/app/shared/tippy.module';
import { ContentPipeModule } from 'src/app/services/content/content-pipe.module';
import { FeatureFlagQuery } from 'src/app/feature-flag/state/feature-flag.query';
import { PermissionsService } from 'src/app/auth/permissions.service';
import { NotificationsStore } from '../state/notifications.store';
import { NotificationsQuery } from '../state/notifications.query';
import { NotificationsService } from '../services/notifications.service';
import { NotificationsPusherService } from '../services/notifications-pusher.service';
import { InAppNotificationsMemoryResetService } from '../services/in-app-notifications-memory-reset.service';
import { NotificationsPanelComponent } from '../notifications-panel/notifications-panel.component';
import { NotificationInboxDto } from '../models/notification.interface';
import { RolePermissionsHelper } from 'src/app/auth/role-permmissions-helper';
import { ElasticSidenavService } from 'src/app/core/app-elastic-sidenav/app-elastic-sidenav.service';

/** Matches the new-nav rail collapse transition (0.2s) + a small buffer so it fully settles. */
const SIDENAV_COLLAPSE_MS = 250;

@Component({
    standalone: true,
    selector: 'app-notifications-bell',
    templateUrl: './notifications-bell.component.html',
    styleUrls: ['./notifications-bell.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, TippyModule, ContentPipeModule, MineIconDirective],
    providers: [NotificationsStore, NotificationsQuery, NotificationsService, NotificationsPusherService],
})
export class NotificationsBellComponent implements OnInit, OnDestroy {

    @HostBinding('class.notifications-bell-host--toast-visible')
    get toastVisibleHostClass(): boolean {
        return this.toastNotification() !== null;
    }

    /** New sidenav variant — renders the bell via `<i mine-icon name="bell">` (bell-s). Old nav unchanged. */
    @Input() newNav = false;

    readonly hasUnread = computed(() => this.notificationsQuery.unreadCount() > 0);
    readonly isBellRinging = signal(false);
    readonly isDotPulsing = signal(false);
    readonly toastNotification = signal<NotificationInboxDto | null>(null);

    private overlayRef: OverlayRef | null = null;

    constructor(
        private overlay: Overlay,
        private elementRef: ElementRef,
        private injector: Injector,
        private destroyRef: DestroyRef,
        private notificationsService: NotificationsService,
        private notificationsQuery: NotificationsQuery,
        private notificationsStore: NotificationsStore,
        private notificationsPusherService: NotificationsPusherService,
        private featureFlagQuery: FeatureFlagQuery,
        private permissionsService: PermissionsService,
        private inAppNotificationsMemoryResetService: InAppNotificationsMemoryResetService,
        private elasticSidenavService: ElasticSidenavService,
    ) {}

    ngOnInit(): void {
        this.inAppNotificationsMemoryResetService.resetRequested$.pipe(
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(() => {
            this.notificationsStore.resetState();
        });

        // Skip the inbox fetch for roles that don't consume notifications
        if (!this.permissionsService.getIsAllowed(RolePermissionsHelper.getPerrmitedRoleForNotifications())) {
            return;
        }
        this.notificationsService.init();
        this.subscribeToNewNotifications();
        this.triggerBellOnInitialUnread();
    }

    ngOnDestroy(): void {
        this.overlayRef?.dispose();
        this.notificationsPusherService.disconnect();
    }

    onBellClick(): void {
        if (this.overlayRef?.hasAttached()) {
            this.closePanel();
            return;
        }
        // Hovering a pinned-collapsed rail expands it as an overlay. Collapse it back first and
        // wait for the transition, so the panel anchors to the bell's settled (collapsed) position
        // and tracks the rail whether it's open or closed.
        const shouldCollapseFirst = this.elasticSidenavService.isCollapsed;
        if (!shouldCollapseFirst) {
            this.openPanel();
            return;
        }
        this.elasticSidenavService.requestCollapse();
        timer(SIDENAV_COLLAPSE_MS).pipe(
            take(1),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(() => this.openPanel());
    }

    onToastClick(): void {
        this.onBellClick();
    }

    private subscribeToNewNotifications(): void {
        this.notificationsService.newNotification$.pipe(
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(notification => {
            this.triggerBellRing();
            this.toastNotification.set(notification);
        });

        this.notificationsService.newNotification$.pipe(
            switchMap(() => timer(5000)),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(() => this.toastNotification.set(null));
    }

    private triggerBellOnInitialUnread(): void {
        toObservable(this.notificationsQuery.loading, { injector: this.injector }).pipe(
            filter(loading => !loading),
            take(1),
            filter(() => this.notificationsQuery.unreadCount() > 0),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(() => this.triggerBellRing());
    }

    private triggerBellRing(): void {
        this.isBellRinging.set(false);
        setTimeout(() => this.isBellRinging.set(true), 0);
        setTimeout(() => this.isBellRinging.set(false), 600);
        this.isDotPulsing.set(true);
        setTimeout(() => this.isDotPulsing.set(false), 1800);
    }

    private openPanel(): void {
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(this.elementRef)
            .withPositions([
                {
                    originX: 'end',
                    originY: 'center',
                    overlayX: 'start',
                    overlayY: 'center',
                    offsetX: 12,
                },
            ]);

        this.overlayRef = this.overlay.create({
            positionStrategy,
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-transparent-backdrop',
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
        });

        const customInjector = Injector.create({
            providers: [
                { provide: NotificationsStore, useValue: this.notificationsStore },
                { provide: NotificationsQuery, useValue: this.notificationsQuery },
                { provide: NotificationsService, useValue: this.notificationsService },
            ],
            parent: this.injector,
        });

        const portal = new ComponentPortal(NotificationsPanelComponent, null, customInjector);
        const componentRef = this.overlayRef.attach(portal);
        componentRef.instance.closePanel.subscribe(() => this.closePanel());
        this.overlayRef.backdropClick().subscribe(() => this.closePanel());
    }

    private closePanel(): void {
        this.overlayRef?.detach();
    }
}
